import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Grid,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import apis from "../utils/apis";
import { Add, Clear, Delete } from "@mui/icons-material";
import Header from "../components/Header";
import FooterComponent from "../components/Footer";
import { styled, alpha } from "@mui/material/styles";
import { getEmail, saveTargetPath } from "../utils/storage";
import toast, { Toaster } from "react-hot-toast";
import { useSelector } from "react-redux";
import { useFeatureFlag } from "@harnessio/ff-react-client-sdk";
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import FitCategoryCell from "../components/common/FitCategoryCell";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import ShortTextIcon from "@mui/icons-material/ShortText";
import { beautifyLabel, getOperatorsForField } from "../utils/CommonFunctions";
import CollapsibleTableNew from "../components/common/CollapsibleTableNew";
import TablePagination from "../components/common/TablePagination";

// Styled components for table
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: "#FDF9E0",
  borderColor: "#FDF9E0",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(39, 140, 55, 0.1)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "transparent",
  },
}));

// Utility function to convert months into a human-readable format
const formatExperience = (totalMonths) => {
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  let experienceText = "";
  if (years > 0) {
    experienceText += `${years} ${years > 1 ? "Years" : "Year"}`;
  }
  if (months > 0) {
    experienceText += ` ${months} ${months > 1 ? "Months" : "Month"}`;
  }
  if (totalMonths === 0) {
    return "Fresher/Intern/Trainee";
  }
  return experienceText.trim();
};

function JdwithDataSearch() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate(); // Initialize useNavigate
  const maxRetries = 2;
  // State for AdvancedDataSearch
  const [fields, setFields] = useState([]);
  const [operators, setOperators] = useState([]);
  const [filterConditions, setFilterConditions] = useState([
    { field: "", operator: "", value: "" },
  ]);
  const [filteredData, setFilteredData] = useState([]);
  const [userData, setUserData] = useState([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(5);
  const [tableLoading, setTableLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // State to toggle ResumeRanker visibility
  const [showRanker, setShowRanker] = useState(false);

  // State for sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarData, setSidebarData] = useState([]);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [sidebarLimit] = useState(20);
  const [sidebarLastId, setSidebarLastId] = useState(null);
  const [sidebarHasMore, setSidebarHasMore] = useState(true);

  // Add recruiter quota state
  const [recruiterQuota, setRecruiterQuota] = useState(null);

  const userEmail = getEmail();
  const userRefId = useSelector((state) => state.userReducer.userRefId);
  const secretEncodeDecodeString = useFeatureFlag("secretEncodeDecodeString");
  const staticRandomEncodeDecodeString = useFeatureFlag(
    "staticRandomEncodeDecodeString"
  );

  // State for validation
  const [validationErrors, setValidationErrors] = useState([]);

  // State for disabling the search button
  const [isSearchDisabled, setIsSearchDisabled] = useState(false);

  // AdvancedDataSearch Effects
  useEffect(() => {
    const fetchDeepSearchParams = async () => {
      try {
        setLoading(true);
        const response = await apis.GET({
          endpoint: "/deep-search-params",
        });

        if (response.status === 201) {
          const deepSearchParams = response.data;
          setFields(deepSearchParams[0]?.fields || []);
          setOperators(deepSearchParams[1]?.operators || []);
        } else {
          throw new Error("Failed to fetch deep search parameters");
        }
      } catch (error) {
        console.error("Error fetching deep search params:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDeepSearchParams();
  }, []);

  // Move fetchRecruiterQuota outside useEffect
  const fetchRecruiterQuota = async () => {
    try {
      const response = await apis.GET({
        endpoint: "/get-recruiter-quota",
      });
      if (response.status === 201) {
        setRecruiterQuota(response.data);
      }
    } catch (error) {
      setRecruiterQuota(null);
    }
  };

  // Fetch recruiter quota on mount
  useEffect(() => {
    fetchRecruiterQuota();
  }, []);

  // Sidebar data fetching function (update to use nextLastId/hasMore)
  const fetchSidebarData = async (reset = false) => {
    if (sidebarLoading || (!sidebarHasMore && !reset)) return;
    setSidebarLoading(true);
    try {
      const params = {
        limit: sidebarLimit,
        ...(sidebarLastId && !reset ? { lastId: sidebarLastId } : {}),
      };
      // Convert params to query string
      const queryString = new URLSearchParams(params).toString();
      const endpoint = `/get-jd-ranking-history?${queryString}`;
      const response = await apis.GET({
        endpoint,
      });
      if (response.status === 201 && Array.isArray(response.data.data)) {
        const newData = response.data.data;

        setSidebarData(reset ? newData : [...sidebarData, ...newData]);
        setSidebarLastId(response.data.nextLastId || null);
        setSidebarHasMore(!!response.data.hasMore);
      } else {
        setSidebarHasMore(false);
      }
    } catch (e) {
      setSidebarHasMore(false);
    } finally {
      setSidebarLoading(false);
    }
  };

  // Sidebar data fetching effect
  useEffect(() => {
    // Always fetch sidebar data on first page load
    if (sidebarData.length === 0) {
      setSidebarLastId(null);
      fetchSidebarData(true);
    }
    // eslint-disable-next-line
  }, []);

  // AdvancedDataSearch Functions
  const addCondition = () => {
    setFilterConditions([
      ...filterConditions,
      { field: "", operator: "", value: "" },
    ]);
    setValidationErrors([...validationErrors, false]); // Add a corresponding validation error state
  };

  const updateCondition = (index, key, value) => {
    const updatedConditions = [...filterConditions];
    updatedConditions[index][key] = value;
    setFilterConditions(updatedConditions);
    if (key === "field") {
      let defaultValue =
        fields.find((f) => f.name === value)?.defaultValue || "";
      updatedConditions[index]["operator"] = defaultValue;
    }

    // Clear validation error for the updated condition
    const updatedValidationErrors = [...validationErrors];
    updatedValidationErrors[index] = false;
    setValidationErrors(updatedValidationErrors);
  };

  const removeCondition = (index) => {
    if (filterConditions.length > 1) {
      const updatedConditions = filterConditions.filter((_, i) => i !== index);
      setFilterConditions(updatedConditions);

      const updatedValidationErrors = validationErrors.filter(
        (_, i) => i !== index
      );
      setValidationErrors(updatedValidationErrors);
    }
  };

  const clearFilters = () => {
    setFilterConditions([{ field: "", operator: "", value: "" }]);
    setValidationErrors([false]); // Reset validation errors
    setFilteredData([]); // Clear the table data
    setShowRanker(false); // Reset the "Show Resume Ranker" button to its default state
    setPage(0); // Reset pagination to the first page
    setLimit(5); // Reset rows per page to the default value
    setTotalCount(0); // Reset total count
  };

  const validateConditions = () => {
    const errors = filterConditions.map(
      (condition) => !condition.field || !condition.operator || !condition.value
    );
    setValidationErrors(errors);
    return !errors.includes(true); // Return true if all conditions are valid
  };

  const handleSearch = async (currentPage = page, currentLimit = limit) => {
    // Trim whitespace from all filter condition values before processing
    const trimmedConditions = filterConditions.map((condition) => ({
      ...condition,
      value:
        typeof condition.value === "string"
          ? condition.value.trim()
          : condition.value,
    }));

    if (!validateConditions()) {
      return; // Stop if validation fails
    }

    try {
      setTableLoading(true);
      setIsSearchDisabled(true); // Disable the search button

      // Prepare payload for the first API call
      const validConditions = trimmedConditions.filter(
        (condition) => condition.field && condition.operator && condition.value
      );

      const payload = {
        filters: validConditions.map((condition) => {
          if (condition.field === "experience") {
            const experienceValue = condition.value.toLowerCase();
            let totalMonths = 0;
            if (/^\d+$/.test(experienceValue)) {
              totalMonths = parseInt(experienceValue, 10) * 12;
            } else {
              const yearsMatch = experienceValue.match(/(\d+)\s*year/);
              const monthsMatch = experienceValue.match(/(\d+)\s*month/);
              if (yearsMatch) totalMonths += parseInt(yearsMatch[1], 10) * 12;
              if (monthsMatch) totalMonths += parseInt(monthsMatch[1], 10);
            }
            return { ...condition, value: totalMonths.toString() };
          }
          return condition;
        }),
        page: currentPage,
        limit: currentLimit,
      };

      if (showRanker) {
        // Call the first API: /deep-search-resume with retry logic
        const deepSearchResumeWithRetry = async (retryCount = 0) => {
          try {
            return await apis.POST({
              endpoint: "/deep-search-resume",
              payload: payload,
              headers: { "Content-Type": "application/json" },
            });
          } catch (err) {
            const status = err?.response?.status || err?.status;
            if ((status === 502 || status === 504) && retryCount < 2) {
              return deepSearchResumeWithRetry(retryCount + 1);
            }
            throw err;
          }
        };

        const resumeResponse = await deepSearchResumeWithRetry();

        if (resumeResponse.status === 201) {
          setFilteredData(resumeResponse.data.data || []);
          setTotalCount(resumeResponse.data.totalCount || 0);
        } else {
          throw new Error(
            "Failed to fetch search results from /deep-search-resume"
          );
        }

        // Prepare rankerPayload for the /deep-search-with-jd-ranker API
        const rankerPayload = {
          filters: payload.filters,
          data: resumeResponse.data.data, // Include the response data from the first API
          currentPage: currentPage, // Use the intended page directly
          pageLimit: currentLimit, // Use currentLimit (rows per page)
        };

        // Call the second API: /deep-search-with-jd-ranker with retry logic
        const deepSearchWithJdRankerWithRetry = async (retryCount = 0) => {
          try {
            return await apis.POST({
              endpoint: "/deep-search-with-jd-ranker",
              payload: rankerPayload,
              headers: { "Content-Type": "application/json" },
            });
          } catch (err) {
            const status = err?.response?.status || err?.status;
            if ((status === 502 || status === 504) && retryCount < 2) {
              return deepSearchWithJdRankerWithRetry(retryCount + 1);
            }
            throw err;
          }
        };

        const rankerResponse = await deepSearchWithJdRankerWithRetry();

        if (rankerResponse.status === 201) {
          // Set the ranked resumes to the table
          setFilteredData(rankerResponse.data.rankedResumes || []);
        } else {
          throw new Error(
            "Failed to fetch ranked results from /deep-search-with-jd-ranker"
          );
        }
      } else {
        // Call the /deep-search-resume API with retry logic
        const deepSearchResumeWithRetry = async (retryCount = 0) => {
          try {
            return await apis.POST({
              endpoint: "/deep-search-resume",
              payload: payload,
              headers: { "Content-Type": "application/json" },
            });
          } catch (err) {
            const status = err?.response?.status || err?.status;
            if ((status === 502 || status === 504) && retryCount < 2) {
              return deepSearchResumeWithRetry(retryCount + 1);
            }
            throw err;
          }
        };

        const resumeResponse = await deepSearchResumeWithRetry();

        if (resumeResponse.status === 201) {
          setFilteredData(resumeResponse.data.data || []);
          setTotalCount(resumeResponse.data.totalCount || 0);
        } else {
          throw new Error(
            "Failed to fetch search results from /deep-search-resume"
          );
        }
      }
    } catch (error) {
      console.error("Error during search:", error);
    } finally {
      setTableLoading(false);
      setIsSearchDisabled(false); // Enable the search button
    }

    // --- Auto update sidebar history after search ---
    // Only refresh if sidebar is open or you want to always refresh
    fetchSidebarData(true);
    await fetchRecruiterQuota();
  };

  const handleChangePage = (event, newPage) => {
    const adjustedPage = newPage;
    setPage(adjustedPage);

    handleSearch(adjustedPage, limit);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    const adjustedPage = Math.floor((page * limit) / newLimit); // Adjust for zero-based index
    setPage(adjustedPage);
    handleSearch(adjustedPage, newLimit);
  };

  const sendEmailToUser = async (row) => {
    try {
      const payload = {
        from: userEmail,
        to: row.userform?.email,
        name: row.name,
        referBy: userRefId,
        subject: "A recruiter is evaluating your resume for an exciting job",
      };

      const response = await apis.POST({
        endpoint: "/send-email-to-user-from-recruiter",
        payload: payload,
        options: { type: "json" },
      });

      if (response.status === 201) {
        toast.success("Mail sent successfully!");
      } else {
        toast.error("Failed to send mail. Please try again.");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send mail. Please try again.");
    }
  };

  // Toggle Resume Ranker and pre-fill conditions
  const toggleRanker = () => {
    if (!showRanker) {
      // Pre-fill conditions when showing Resume Ranker
      setFilterConditions([
        { field: "primarySkill.name", operator: "contains", value: "" },
        { field: "role", operator: "contains", value: "" },
        { field: "location", operator: "contains", value: "" },
        { field: "experience", operator: ">=", value: "" },
      ]);
    } else {
      // Reset to default conditions when hiding Resume Ranker
      setFilterConditions([{ field: "", operator: "", value: "" }]);
    }

    // Clear table data and reset pagination
    setFilteredData([]); // Clear the table data
    // setPage(1); // Reset pagination to the first page
    setLimit(5); // Reset rows per page to the default value
    setTotalCount(0); // Reset total count

    setShowRanker(!showRanker); // Toggle the showRanker state
  };

  const handleFormSubmit = (e) => {
    e.preventDefault(); // Prevent default form submission
    handleSearch(1, limit); // Trigger the search function
  };

  const CustomPaper = React.forwardRef(function CustomPaper(props, ref) {
    return (
      <Paper
        ref={ref}
        {...props}
        sx={{
          backgroundColor: "#231F20",
          color: "#FDF9E0",
        }}
      />
    );
  });

  // Dynamically add columns based on the ranker state
  const columns = [
    { field: "name", headerName: "Name", width: 200 },
    { field: "location", headerName: "Location", width: 200 },
    { field: "experience", headerName: "Experience", width: 150 },
    { field: "role", headerName: "Role", width: 200 },
    ...(showRanker
      ? [
          { field: "fitCategory", headerName: "Fit Category", width: 150 },
          { field: "fitPercentage", headerName: "Fit %", width: 150 },
        ]
      : []),
  ];

  // Sidebar list item click handler: prefill filters and show ranker
  const handleSidebarItemClick = (item) => {
    if (item.filters && Array.isArray(item.filters)) {
      setFilterConditions(
        item.filters.map((f) => {
          if (f.field === "experience") {
            // Convert months back to human readable format for prefill
            const months = Number(f.value);
            if (isNaN(months) || months === 0)
              return { ...f, value: "Fresher/Intern/Trainee" };
            const years = Math.floor(months / 12);
            const remMonths = months % 12;
            let result = [];
            if (years > 0)
              result.push(`${years} ${years > 1 ? "Years" : "Year"}`);
            if (remMonths > 0)
              result.push(`${remMonths} ${remMonths > 1 ? "Months" : "Month"}`);
            return { ...f, value: result.join(" ") };
          }
          return { ...f };
        })
      );
      setShowRanker(true);
      setSidebarOpen(false);
      // setPage(1);
      setLimit(5);
      setTotalCount(0);
      // Prefill results directly from item if present (expecting item.data to be an array of result objects)
      if (item.results && Array.isArray(item.results)) {
        setFilteredData(item.results);
        setTotalCount(item.results.length);
      } else {
        setFilteredData([]);
        setTotalCount(0);
      }
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#000",
        minHeight: "100vh",
        width: "100%",
        color: "#FDF9E0",
        backgroundImage: "url('../../assets/images/homepage-background.svg')",
        backgroundRepeat: "repeat",
        backgroundSize: "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: isMobile ? 2 : 4,
      }}
    >
      <Toaster />
      <Header />

      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress size={50} />
          <Typography sx={{ mt: 2, color: "#FDF9E0" }}>Loading...</Typography>
        </Box>
      ) : (
        <>
          {/* Sidebar Drawer */}
          <Drawer
            anchor="left"
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            PaperProps={{
              sx: {
                width: isMobile ? "80vw" : 340,
                backgroundColor: "#18181A",
                color: "#FDF9E0",
                pt: 2,
                pb: 2,
                borderRight: "1px solid #333",
              },
            }}
          >
            <Box
              sx={{
                px: 2,
                pb: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, fontFamily: "Kanit" }}
              >
                JD Ranking History
              </Typography>
              <IconButton
                onClick={() => setSidebarOpen(false)}
                sx={{ color: "#FDF9E0" }}
              >
                <Clear />
              </IconButton>
            </Box>
            <Divider sx={{ background: "#333" }} />
            <List
              sx={{
                px: 1,
                py: 0,
                overflowY: "auto",
                maxHeight: "80vh",
              }}
            >
              {sidebarData.length === 0 && !sidebarLoading && (
                <ListItem
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 120,
                    width: "100%",
                  }}
                >
                  <ListItemText
                    primary="No history found."
                    sx={{ textAlign: "center" }}
                  />
                </ListItem>
              )}
              {sidebarData.map((item, idx) => (
                <ListItem
                  button
                  key={item.id || item._id || idx}
                  onClick={() => handleSidebarItemClick(item)}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    backgroundColor: alpha("#26801E", 0.08),
                    "&:hover": { backgroundColor: alpha("#26801E", 0.18) },
                    transition: "background 0.2s",
                    alignItems: "flex-start",
                  }}
                >
                  <ListItemText
                    primary={
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: 15,
                            color: "#FDF9E0",
                          }}
                        >
                          {(() => {
                            // Group filters by field
                            const fieldMap = {
                              "primarySkill.name": "Skills",
                              role: "Role",
                              location: "Location",
                              experience: "Experience",
                            };
                            const grouped = {};
                            if (item.filters && item.filters.length) {
                              item.filters.forEach((f) => {
                                const key = fieldMap[f.field] || f.field;
                                if (!grouped[key]) grouped[key] = [];
                                // Format experience value if field is experience
                                if (f.field === "experience") {
                                  grouped[key].push(
                                    formatExperience(Number(f.value))
                                  );
                                } else {
                                  grouped[key].push(f.value);
                                }
                              });
                              // Build summary string
                              return Object.entries(grouped)
                                .map(
                                  ([key, values]) =>
                                    `${key}: ${values.join(", ")}`
                                )
                                .join(", ");
                            }
                            return "No filters";
                          })()}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#aaa" }}>
                          {item.createdAt
                            ? new Date(item.createdAt).toLocaleString()
                            : ""}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
              {sidebarLoading && (
                <ListItem
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 120,
                    width: "100%",
                  }}
                >
                  <CircularProgress size={24} sx={{ color: "#FDF9E0" }} />
                </ListItem>
              )}
            </List>
            {sidebarHasMore && !sidebarLoading && (
              <Box sx={{ textAlign: "center", py: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => fetchSidebarData()}
                  sx={{
                    color: "#FDF9E0",
                    borderColor: "#FDF9E0",
                    "&:hover": { borderColor: "#26801E", color: "#26801E" },
                  }}
                >
                  Load More
                </Button>
              </Box>
            )}
          </Drawer>

          {/* Add before <Typography> Advanced JD Search */}
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", md: "100%", xl: "90%" },
              mx: "auto",
              mb: { xs: "55px" },
              mt: { xs: "80px", md: "55px" },
              display: "flex",
              alignItems: "center",
              background: "#18181A",
              borderRadius: 2,
              boxShadow: 2,
              px: { xs: 1, sm: 2 },
              py: { xs: 1, sm: 2 },
              justifyContent: "space-between",
              flexWrap: { xs: "wrap", sm: "wrap", md: "nowrap" },
              position: "relative",
              minHeight: 70,
              flexDirection: { xs: "column", sm: "column", md: "row" },
              gap: { xs: 2, sm: 6, md: 0 },
            }}
          >
            {/* Sidebar Drawer Icon (left) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: { xs: "unset", sm: 1 },
                width: { xs: "100%", sm: "auto" },
                mb: { xs: 1, sm: 0 },
                justifyContent: { xs: "center", sm: "flex-start" },
              }}
            >
              <IconButton
                onClick={() => setSidebarOpen(true)}
                sx={{
                  color: "#FDF9E0",
                  backgroundColor: "#231F20",
                  borderRadius: "8px",
                  boxShadow: 2,
                  "&:hover": { backgroundColor: "#333" },
                  mr: 2,
                }}
              >
                {isMobile ? (
                  <ShortTextIcon fontSize="large" />
                ) : (
                  <ViewSidebarIcon fontSize="large" />
                )}
              </IconButton>
              <Typography sx={{ fontWeight: 600, color: "#FDF9E0" }}>
                History
              </Typography>
            </Box>
            {/* Centered Advanced JD Search title */}
            <Box
              sx={{
                position: { xs: "static", sm: "absolute" },
                left: { sm: "50%" },
                top: { sm: "50%" },
                transform: { sm: "translate(-50%, -50%)" },
                zIndex: 2,
                pointerEvents: "none",
                minWidth: 200,
                textAlign: "center",
                width: { xs: "100%", sm: "auto" },
                mb: { xs: 1, sm: 0 },
              }}
            >
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{
                  fontWeight: 700,
                  fontFamily: "Kanit",
                  color: "#FDF9E0",
                  letterSpacing: 1,
                }}
              >
                JD Ranker
              </Typography>
            </Box>
            {/* Quota counts (right) */}
            <Box
              sx={{
                display: "flex",
                gap: { xs: 2, sm: 3 },
                alignItems: "center",
                flexWrap: "wrap",
                flex: { xs: "unset", sm: 1 },
                justifyContent: { xs: "center", sm: "flex-end" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Box sx={{ minWidth: 80, textAlign: "center" }}>
                <Typography sx={{ color: "#aaa", fontSize: 13 }}>
                  JD Ranker
                </Typography>
                <Typography
                  sx={{
                    color: "#FDF9E0",
                    fontWeight: 700,
                    fontSize: 18,
                  }}
                >
                  {recruiterQuota?.deepSearchCount ?? "-"}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 80, textAlign: "center" }}>
                <Typography sx={{ color: "#aaa", fontSize: 13 }}>
                  Resume Ranker (File Based)
                </Typography>
                <Typography
                  sx={{
                    color: "#FDF9E0",
                    fontWeight: 700,
                    fontSize: 18,
                  }}
                >
                  {recruiterQuota?.resumeRankerCount ?? "-"}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 80, textAlign: "center" }}>
                <Typography sx={{ color: "#aaa", fontSize: 13 }}>
                  Processed
                </Typography>
                <Typography
                  sx={{
                    color: "#FDF9E0",
                    fontWeight: 700,
                    fontSize: 18,
                  }}
                >
                  {recruiterQuota?.processedCount ?? "-"}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={toggleRanker}
            sx={{
              mb: 4,
              textTransform: "none",
              borderRadius: "8px",
              backgroundColor: "#26801E",
              "&:hover": {
                backgroundColor: "#1E6A16",
              },
            }}
          >
            {showRanker ? "Hide JD Details" : "Enter JD Details"}
          </Button>
          {/* AdvancedDataSearch UI */}
          <form
            onSubmit={handleFormSubmit}
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {filterConditions.map((condition, index) => (
              <Grid
                container
                spacing={2}
                key={index}
                sx={{
                  mb: 2,
                  maxWidth: { xs: "100%", md: "100%", xl: "80%" },
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                }}
              >
                <Grid item xs={12} sm={3}>
                  <Autocomplete
                    options={fields}
                    getOptionLabel={(option) => {
                      return beautifyLabel(option?.displayName);
                    }}
                    value={
                      fields.find((f) => f.name === condition.field) || null
                    }
                    onChange={(event, newValue) =>
                      updateCondition(
                        index,
                        "field",
                        newValue ? newValue.name : ""
                      )
                    }
                    PaperComponent={CustomPaper}
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: "offset",
                            options: { offset: [0, 10] },
                          },
                        ],
                      },
                      paper: {
                        sx: {
                          "& .MuiAutocomplete-noOptions": {
                            backgroundColor: "#231F20",
                            color: "#FDF9E0",
                          },
                        },
                      },
                      listbox: {
                        sx: {
                          backgroundColor: "#231F20",
                          "& .MuiAutocomplete-option": {
                            color: "#FDF9E0",
                            "&:hover": {
                              backgroundColor: alpha("#FDF9E0", 0.1),
                            },
                            "&.Mui-selected": {
                              backgroundColor: alpha("#FDF9E0", 0.2),
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: alpha("#FDF9E0", 0.25),
                            },
                          },
                          "& .MuiAutocomplete-noOptions": {
                            color: "#FDF9E0",
                          },
                        },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Field"
                        placeholder="Select Field"
                        error={validationErrors[index] && !condition.field}
                        helperText={
                          validationErrors[index] && !condition.field
                            ? "Field is required"
                            : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#231F20",
                            "& fieldset": {
                              borderColor: "#FDF9E0",
                            },
                            "&:hover fieldset": {
                              borderColor: "#FDF9E0",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#FDF9E0",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "#FDF9E0",
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#FDF9E0",
                          },
                          "& .MuiInputBase-input": {
                            color: "#FDF9E0",
                          },
                          "& .MuiFormHelperText-root": {
                            color: "#FDF9E0",
                          },
                          "& svg": {
                            fill: "#FDF9E0 !important",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Autocomplete
                    options={getOperatorsForField(condition, fields)}
                    getOptionLabel={(option) => option}
                    value={condition.operator || ""}
                    onChange={(event, newValue) =>
                      updateCondition(index, "operator", newValue || "")
                    }
                    PaperComponent={CustomPaper}
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: "offset",
                            options: { offset: [0, 10] },
                          },
                        ],
                      },
                      paper: {
                        sx: {
                          "& .MuiAutocomplete-noOptions": {
                            backgroundColor: "#231F20",
                            color: "#FDF9E0",
                          },
                        },
                      },
                      listbox: {
                        sx: {
                          backgroundColor: "#231F20",
                          "& .MuiAutocomplete-option": {
                            color: "#FDF9E0",
                            "&:hover": {
                              backgroundColor: alpha("#FDF9E0", 0.1),
                            },
                            "&.Mui-selected": {
                              backgroundColor: alpha("#FDF9E0", 0.2),
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: alpha("#FDF9E0", 0.25),
                            },
                          },
                          "& .MuiAutocomplete-noOptions": {
                            color: "#FDF9E0",
                          },
                        },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Operator"
                        placeholder="Select Operator"
                        error={validationErrors[index] && !condition.operator}
                        helperText={
                          validationErrors[index] && !condition.operator
                            ? "Operator is required"
                            : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#231F20",
                            "& fieldset": {
                              borderColor: "#FDF9E0",
                            },
                            "&:hover fieldset": {
                              borderColor: "#FDF9E0",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#FDF9E0",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "#FDF9E0",
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#FDF9E0",
                          },
                          "& .MuiInputBase-input": {
                            color: "#FDF9E0",
                          },
                          "& .MuiFormHelperText-root": {
                            color: "#FDF9E0",
                          },
                          "& svg": {
                            fill: "#FDF9E0 !important",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    value={condition.value}
                    onChange={(e) =>
                      updateCondition(index, "value", e.target.value)
                    }
                    placeholder={
                      fields.find((f) => f.name === condition.field)
                        ?.placeholder || "Enter value"
                    }
                    error={validationErrors[index] && !condition.value}
                    helperText={
                      validationErrors[index] && !condition.value
                        ? "Value is required"
                        : ""
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#231F20",
                        "& fieldset": {
                          borderColor: "#FDF9E0",
                        },
                        "&:hover fieldset": {
                          borderColor: "#FDF9E0",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#FDF9E0",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#FDF9E0",
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#FDF9E0",
                      },
                      "& .MuiInputBase-input": {
                        color: "#FDF9E0",
                      },
                      "& .MuiFormHelperText-root": {
                        color: "#FDF9E0",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={1} sx={{ textAlign: "center" }}>
                  <IconButton
                    onClick={() => removeCondition(index)}
                    disabled={filterConditions.length === 1}
                    color="error"
                    sx={{
                      "&.Mui-disabled": {
                        color: "#333",
                      },
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Box
              sx={{
                width: "100%",
                display: "flex",
                gap: 2,
                mt: 3,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={addCondition}
              >
                Add Condition
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Clear />}
                onClick={clearFilters}
              >
                Clear Filters
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="success"
                disabled={
                  isSearchDisabled ||
                  filterConditions.some(
                    (condition) =>
                      !condition.field ||
                      !condition.operator ||
                      !condition.value
                  )
                }
                sx={{
                  "&.Mui-disabled": {
                    backgroundColor: "#333",
                    color: "#000",
                  },
                }}
              >
                Search
              </Button>
            </Box>
          </form>
          {/* <Box> */}
          <Paper
            sx={{
              backgroundColor: "transparent",
              display: "flex",
              flexDirection: "column",
              // justifyContent: "center",
              // alignItems: "center",
              mt: 5,
              width: "100%",
              maxWidth: { xs: "100%", md: "100%", xl: "90%" },
            }}
          >
            <TableContainer
              sx={{
                border: "1px solid #FDF9E0",
                borderRadius: "0px",
                overflowX: "auto",
              }}
            >
              <CollapsibleTableNew
                rows={filteredData}
                tableLoading={tableLoading}
                searchQuery={null}
                rowsPerPage={limit}
                page={page}
                collapsible={true}
                tableName={showRanker ? "jd-ranker" : "normal-ranker"}
              />
            </TableContainer>

            <TablePagination
              page={page}
              rowsPerPage={limit}
              totalCount={totalCount}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              tableLoading={tableLoading}
            />
          </Paper>
          {/* </Box> */}
        </>
      )}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/Upload-Resume")} // Redirect to the upload route
          sx={{
            color: "#FDF9E0",
            textTransform: "none",
            borderRadius: "8px",
            padding: "10px 20px",
            "&:hover": {
              backgroundColor: "#1E6A16",
            },
          }}
        >
          Back to Upload
        </Button>
      </Box>
      {/* <Box sx={{ pt: showRanker ? "25%" : "0" }}>
        <FooterComponent />
      </Box> */}
    </Box>
  );
}

export default JdwithDataSearch;
